// Live locations — the body of the Attendance page's Live tracking tab: the
// people panel (avatar, name, last ping or "Offline · time", client-side
// search) beside the Google map (LiveLocationsMap). Every linked user lists;
// only those with a known position pin. Selection is shared: clicking a row
// pans the map to that person, clicking a pin highlights the row; map
// background click or re-click clears it. "Pull latest position" sits on the
// map and asks every linked app for a fresh fix. The user's own node areas
// draw under the pins (every positioned node); the "All areas" dropdown
// in the map's inline-start corner is a jump-to control — picking an area centers the
// map on it (nothing is filtered), and its "All areas" row clears the pick
// and fits the map around every area instead. The design's panel filter button is omitted
// by user decision (no filter behaviour is defined, and inert affordances
// are never drawn). Rows keep API order.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Button, Card, Icon, Input, SelectDropdown } from '@/components/common';
import type { LiveUser, NodeArea, PositionedLiveUser } from '@/api/endpoints/liveTrackingApi';
import { MAP_API_KEY } from '@/config/config';
import toneOf from '@/utils/avatarTone';
import { fmtClock } from './utils/lastSeen';
import LiveLocationsMap from './LiveLocationsMap';
import initialsOf from '@/utils/initials';

interface LiveLocationsCardProps {
  users: LiveUser[];
  /** The user's own node areas — drawn under the pins, never fitted to. */
  areas: NodeArea[];
  /** Already-formatted "as of" clock time of the data. */
  asOfLabel: string;
  /** "Pull latest position" — asks every linked user's app for a fresh fix. */
  onPullLatest: () => void;
  pulling: boolean;
  /** Live and someone on the clock — otherwise the control is disabled. */
  canPull: boolean;
  /** The pin card's "Show route history" — opens that person's route drawer. */
  onShowRoute: (companyUserId: string) => void;
  /** Extra class on the card — the tab stretches it. */
  className?: string;
}

/** The areas dropdown's reset row — node ids are hex, so no collision. */
const ALL_AREAS = 'all';

const LiveLocationsCard = ({
  users,
  areas,
  asOfLabel,
  onPullLatest,
  pulling,
  canPull,
  onShowRoute,
  className,
}: LiveLocationsCardProps) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // "Show all" — each bump asks the map to re-fit around every known position.
  const [fitSignal, setFitSignal] = useState(0);
  // The picked area; the signal bumps on EVERY pick (SelectDropdown reports a
  // re-pick of the same row too), so picking an area again re-centers.
  const [areaId, setAreaId] = useState<string | null>(null);
  const [areaSignal, setAreaSignal] = useState(0);

  const needle = query.trim().toLowerCase();
  const visible = needle ? users.filter((u) => u.fullName.toLowerCase().includes(needle)) : users;
  const positioned = users.filter((u): u is PositionedLiveUser => u.lastLocation !== null);
  const showMap = Boolean(MAP_API_KEY) && positioned.length > 0;
  // A pick that vanished on refetch falls back to the reset row.
  const areaValue = areas.some((a) => a.id === areaId) ? (areaId as string) : ALL_AREAS;
  const areaOptions = [
    { value: ALL_AREAS, label: t('attendance_live_all_areas') },
    ...areas.map((a) => ({ value: a.id, label: a.name })),
  ];
  const pickArea = (next: string) => {
    setAreaId(next === ALL_AREAS ? null : next);
    setAreaSignal((n) => n + 1);
  };

  /** The row's second line: the last ping while online, "Offline · time"
   *  (or just "Offline") otherwise. A bare dash marks an online user whose
   *  app has not reported yet. */
  const rowTime = (u: LiveUser) => {
    if (u.online) return u.lastPingAt ? fmtClock(u.lastPingAt) : '—';
    return u.lastPingAt
      ? t('attendance_live_offline_at', { time: fmtClock(u.lastPingAt) })
      : t('attendance_live_offline');
  };

  return (
    <Card padded={false} className={['attendance-live', className].filter(Boolean).join(' ')}>
      <div className='attendance-live__panel'>
        <div className='attendance-live__head'>
          <h2 className='attendance-live__title'>
            <Icon name='map-pin' size={18} aria-hidden='true' />
            {t('attendance_live_title')}
          </h2>
          <span className='attendance-live__meta'>
            {t('attendance_live_people_asof', { count: users.length, time: asOfLabel })}
          </span>
        </div>

        <div className='attendance-live__search'>
          <Input
            size='sm'
            iconStart='search'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('attendance_live_search')}
            aria-label={t('attendance_live_search')}
          />
        </div>

        {visible.length === 0 ? (
          <div className='attendance-live__empty'>
            <Icon name={users.length === 0 ? 'map-pin' : 'search'} size={22} aria-hidden='true' />
            {t(users.length === 0 ? 'attendance_live_empty' : 'attendance_live_no_match')}
          </div>
        ) : (
          <ul className='attendance-live__list'>
            {visible.map((u) => {
              const selected = u.companyUserId === selectedId;
              return (
                <li key={u.companyUserId}>
                  <button
                    type='button'
                    className={[
                      'attendance-live__row',
                      !u.online && 'attendance-live__row--offline',
                      selected && 'is-selected',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-pressed={selected}
                    onClick={() => setSelectedId(selected ? null : u.companyUserId)}
                  >
                    <Avatar initials={initialsOf(u.fullName)} size={36} tone={toneOf(u.fullName)} />
                    <span className='attendance-live__row-text'>
                      <span className='attendance-live__row-name'>{u.fullName}</span>
                      <span className='attendance-live__row-time'>{rowTime(u)}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* The map cell owns the overlay control so it stays reachable while
          the quiet placeholder (no key, no known positions) is up. */}
      <div
        className={['attendance-live__map', !showMap && 'attendance-live__map--placeholder']
          .filter(Boolean)
          .join(' ')}
      >
        {showMap ? (
          <LiveLocationsMap
            users={positioned}
            areas={areas}
            selectedId={selectedId}
            onSelect={setSelectedId}
            fitSignal={fitSignal}
            focusAreaId={areaId}
            focusSignal={areaSignal}
            onShowRoute={onShowRoute}
          />
        ) : (
          <Icon name='map-pin' size={28} aria-hidden='true' />
        )}
        {/* "All areas" sits in the inline-start corner, the buttons in the
            inline-end one. Portal: the card clips overflow, the panel must
            escape it. */}
        <div className='attendance-live__map-areas'>
          <SelectDropdown<string>
            size='sm'
            iconStart='map-pin'
            options={areaOptions}
            value={areaValue}
            onChange={pickArea}
            aria-label={t('attendance_live_areas')}
            portal
            flip
            width={240}
            disabled={!showMap || areas.length === 0}
          />
        </div>
        <div className='attendance-live__map-controls'>
          <Button
            variant='secondary'
            size='sm'
            iconStart='maximize'
            disabled={!showMap}
            onClick={() => setFitSignal((n) => n + 1)}
          >
            {t('attendance_live_show_all')}
          </Button>
          <Button
            variant='secondary'
            size='sm'
            iconStart='refresh-cw'
            loading={pulling}
            disabled={!canPull}
            onClick={onPullLatest}
          >
            {t('attendance_live_pull_latest')}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default LiveLocationsCard;
